import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import JuicyOrange from "@/components/site/JuicyOrange";
import { X } from "lucide-react";

/**
 * OrangeOptimisePromoModal — homepage-only discovery nudge for the free
 * Orange Optimise health scan.
 *
 * Behaviour:
 *   • Never on /optimise, /hq, /pay, /fix, /optimise/report, /unsubscribe.
 *   • Delayed reveal (≈8s) after mount.
 *   • Suppressed for 7 days after Close or CTA — localStorage.
 *   • Escape / outside click / X all dismiss.
 *   • Respects prefers-reduced-motion.
 *   • No backend calls, no records created.
 */
const SUPPRESS_KEY = "obs.optimise.promo.dismissed_at";
const SUPPRESS_MS  = 7 * 24 * 60 * 60 * 1000;
const DELAY_MS     = 8000;

const EXCLUDED_PREFIXES = ["/optimise", "/hq", "/pay", "/fix", "/unsubscribe"];

export default function OrangeOptimisePromoModal() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const pathExcluded = EXCLUDED_PREFIXES.some((p) => location.pathname.startsWith(p));

  const dismiss = useCallback(() => {
    try { window.localStorage.setItem(SUPPRESS_KEY, String(Date.now())); } catch {}
    setOpen(false);
  }, []);

  useEffect(() => {
    if (pathExcluded) return undefined;
    let dismissedAt = 0;
    try { dismissedAt = parseInt(window.localStorage.getItem(SUPPRESS_KEY) || "0", 10) || 0; } catch {}
    if (Date.now() - dismissedAt < SUPPRESS_MS) return undefined;
    const t = window.setTimeout(() => setOpen(true), DELAY_MS);
    return () => window.clearTimeout(t);
  }, [pathExcluded]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") dismiss(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, dismiss]);

  const goToOptimise = () => {
    try { window.localStorage.setItem(SUPPRESS_KEY, String(Date.now())); } catch {}
    setOpen(false);
    navigate("/optimise");
  };

  if (!open || pathExcluded) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Free website health scan"
      data-testid="optimise-promo-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6 bg-black/60"
      onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
    >
      <style>{`
        @media (prefers-reduced-motion: no-preference) {
          .obs-promo-in { animation: obs-promo-in 320ms ease-out both; }
          .obs-promo-arrow { animation: obs-promo-slide 500ms ease-out 200ms both; }
        }
        @keyframes obs-promo-in {
          from { opacity: 0; transform: translateY(12px) scale(0.98); }
          to   { opacity: 1; transform: none; }
        }
        @keyframes obs-promo-slide {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: none; }
        }
      `}</style>

      <div className="obs-promo-in relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl bg-white shadow-2xl border-2 border-black">
        <button
          onClick={dismiss}
          data-testid="optimise-promo-close"
          aria-label="Close promotional modal"
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-[#F6F5F2] focus:outline-none focus:ring-2 focus:ring-black"
        >
          <X size={18} />
        </button>

        <div className="p-6 md:p-10">
          <div className="text-[10px] tracking-[0.32em] uppercase font-black text-[#FF6A00]">Orange Optimise</div>
          <h2 className="mt-2 font-black text-3xl md:text-5xl leading-[1.05] tracking-[-0.03em]"
              data-testid="optimise-promo-title">
            How healthy<br/>is your website?
          </h2>
          <p className="mt-3 text-[11px] md:text-[12px] tracking-[0.28em] uppercase font-black text-black"
             data-testid="optimise-promo-subhead">
            <span className="text-[#FF6A00]">FREE</span> WEBSITE HEALTH SCAN
          </p>

          {/* Visual flow: Website → Optimise → Juicy Orange with score */}
          <div className="mt-6 grid grid-cols-3 items-center gap-2 md:gap-4">
            <div className="flex flex-col items-center gap-1.5 text-center">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl border-2 border-black flex items-center justify-center font-black text-lg">
                🌐
              </div>
              <div className="text-[9px] tracking-[0.18em] uppercase font-black">Website</div>
            </div>
            <div className="flex flex-col items-center gap-1.5 text-center obs-promo-arrow">
              <div className="text-2xl md:text-3xl font-black text-[#FF6A00]">→</div>
              <div className="text-[9px] tracking-[0.18em] uppercase font-black text-[#FF6A00]">Orange Optimise</div>
            </div>
            <div className="flex flex-col items-center gap-1.5 text-center">
              <JuicyOrange score={87} size={120} testId="optimise-promo-orange" />
              <div className="text-[9px] tracking-[0.18em] uppercase font-black">Orange Score</div>
            </div>
          </div>

          <p className="mt-6 text-[14px] text-black/80 leading-relaxed">
            Find out how your website is performing in seconds. Orange Optimise
            checks the public-facing health of your website and gives you a
            clear Orange Score, identifies potential issues and shows you what
            could be improved.
          </p>

          <ul className="mt-5 grid grid-cols-2 gap-2 text-[13px]" data-testid="optimise-promo-checks">
            {["Performance", "Mobile experience", "Assets & images",
              "Technical setup", "Security health", "Efficiency"].map((c) => (
              <li key={c} className="flex items-start gap-2">
                <span className="mt-0.5 text-[#0FA958] font-black">✓</span>
                <span>{c}</span>
              </li>
            ))}
          </ul>

          <div className="mt-6 flex flex-wrap gap-2">
            {["No account", "No payment", "Free to scan"].map((t) => (
              <span key={t} className="inline-flex items-center px-3 py-1 rounded-full text-[10px] font-black tracking-[0.18em] uppercase bg-[#FFF7EE] text-[#8A2E00] border border-[#FCE1C7]">
                {t}
              </span>
            ))}
          </div>

          <div className="mt-8 flex justify-center">
            <button
              type="button"
              onClick={goToOptimise}
              data-testid="optimise-promo-cta"
              className="inline-flex items-center justify-center px-10 py-4 rounded-full bg-black text-white font-black text-[13px] tracking-[0.22em] uppercase hover:bg-[#1A1A1A] focus:outline-none focus:ring-4 focus:ring-black/30 shadow-lg"
            >
              Scan my website for free
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
